import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { generateApiKey, hashApiKey } from "../../../../shared/apiKeys";
import { OAUTH_SERVICE_ACCOUNTS_FEATURE_FLAG } from "../../../../shared/featureFlagNames";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { useHasAccess } from "../../../hooks/useHasAccess";
import { useMouseDownOutside } from "../../../hooks/useMouseDownOutside";
import { useQuery } from "../../../hooks/useQuery";
import { useResolveScopeName } from "../../../hooks/useResolveScopeName";
import { useTenantRoles } from "../../../hooks/useTenantRoles";
import { formatDateShort } from "../../../lib/formatTimestamp";
import { Box } from "../../Box";
import { Button } from "../../Button";
import { ConfirmModal } from "../../ConfirmModal";
import { Icon } from "../../Icon";
import { Input } from "../../Input";
import { ListTable } from "../../ListTable";
import { ServiceAccountAvatar } from "../../ServiceAccountAvatar";
import { makeUrl, routes } from "../../../lib/urls";
import { TabbedModal, type ModalTab } from "../../TabbedModal";
import { TenantRolesChecklist } from "../../TenantRolesChecklist";
import { Text } from "../../Text";
import {
  AnchoredListPopover,
  Card,
  FieldLabel,
  ListBox,
  MatchRow,
  MatchSectionLabel,
  NeutralIconBadge,
  PlainInput,
  SearchBar,
  SearchIconWrap,
  SearchInputWrap,
  SectionHeading,
} from "../shared";
import { DangerSection } from "../DangerSection";
import { collectEffectiveGrants } from "../lib/collectEffectiveGrants";
import { useOpenResource } from "../lib/useOpenResource";
import { PrincipalAccessTable } from "../PrincipalAccessTable";
import { CreateApiKeyModal } from "../ApiKeysSettings/CreateApiKeyModal";
import { AddFederationModal } from "./AddFederationModal";
import { GetTemporaryCredentialModal } from "./GetTemporaryCredentialModal";

const NameCell = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const NameCellText = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const SectionHeadingRow = styled("div")`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const SectionHeadingText = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const MatchIcon = styled("span")`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
`;

const TabPanel = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

type BufferedKey = { id: string; name: string; start: string; key: string };
type BufferedFed = {
  id: string;
  providerId: string;
  providerName: string;
  subject: string;
  name: string;
};

type RenderKey = {
  id: string;
  name: string | null;
  start: string | null;
  createdAt: number | null;
  lastRequest: number | null;
  expiresAt: number | null;
};

type RenderFed = {
  id: string;
  name: string;
  providerName: string;
  subject: string;
  lastUsedAt: number | null;
};

type Tab = "settings" | "access";

export function ServiceAccountDetail({
  serviceAccountId,
  onClose,
}: {
  serviceAccountId: string | null;
  onClose: () => void;
}) {
  const z = useZero();
  // In create mode (serviceAccountId === null) nothing is persisted until
  // "Create service account": the name, keys, and federations are buffered
  // locally and written in one go, since keys/federations reference a service
  // account id that doesn't exist yet. Access grants are not buffered: a
  // service account is granted access from the resource it's given access to
  // (a space, connector, …), so there's nothing to grant until the service
  // account exists. `newId` is generated up front so the avatar colour is
  // stable.
  const isCreateMode = serviceAccountId === null;
  const [newId] = useState(() => nanoid(12));
  const [draft, setDraft] = useState<string | null>(null);
  const [bufferedKeys, setBufferedKeys] = useState<BufferedKey[]>([]);
  const [bufferedFeds, setBufferedFeds] = useState<BufferedFed[]>([]);
  // In create mode the service account has no id yet, so authorized apps are
  // staged here and written after it is created (mirrors bufferedFeds).
  const [bufferedOauthAppIds, setBufferedOauthAppIds] = useState<string[]>([]);

  const [accounts] = useQuery(queries.serviceAccounts());
  const account = serviceAccountId
    ? accounts.find((a) => a.id === serviceAccountId)
    : undefined;
  const canManage = useHasAccess(
    "service_account:manage",
    serviceAccountId ?? undefined
  );
  const canAssume = useHasAccess(
    "service_account:assume",
    serviceAccountId ?? undefined
  );
  const canAssignTenantRoles = useHasAccess("tenant:roles_manage");
  const canEdit = isCreateMode || canManage;
  const [grants] = useQuery(
    queries.roleGrantsByPrincipalServiceAccount({
      serviceAccountId: serviceAccountId ?? "",
    })
  );
  const [customRoles] = useQuery(queries.allRolesLite());
  const [apps] = useQuery(queries.connectedAppsIndex());
  const { resolveScopeName } = useResolveScopeName();
  const { openResource } = useOpenResource();
  const [keys] = useQuery(
    queries.serviceAccountKeys({ serviceAccountId: serviceAccountId ?? "" })
  );
  const oauthEnabled = useFeatureFlag(OAUTH_SERVICE_ACCOUNTS_FEATURE_FLAG);
  const [oauthApps] = useQuery(queries.oauthApps());
  const [oauthAppAuths] = useQuery(
    queries.oauthAppAuthorizationsForServiceAccount({
      serviceAccountId: serviceAccountId ?? "",
    })
  );

  const [showGenerateKey, setShowGenerateKey] = useState(false);
  const [showTempCredential, setShowTempCredential] = useState(false);
  const [showAddFederation, setShowAddFederation] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>("settings");

  const [oauthSearch, setOauthSearch] = useState("");
  const [showOauthMatches, setShowOauthMatches] = useState(false);
  const oauthInputRef = useRef<HTMLInputElement | null>(null);
  const [oauthSearchRef, oauthPopoverRef] = useMouseDownOutside(() =>
    setShowOauthMatches(false)
  );
  const nameRef = useRef<HTMLInputElement>(null);

  const [confirm, setConfirm] = useState<{
    title: string;
    description: string;
    confirmLabel: string;
    onConfirm: () => void;
  } | null>(null);

  // Autofocus the name when creating; the dialog grabs focus on open.
  useEffect(() => {
    if (!isCreateMode) return;
    const id = requestAnimationFrame(() => nameRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [isCreateMode]);

  const customRoleById = useMemo(
    () => new Map(customRoles.map((role) => [role.id, role])),
    [customRoles]
  );
  const appsById = useMemo(() => new Map(apps.map((a) => [a.id, a])), [apps]);
  const accessGrants = useMemo(
    () => collectEffectiveGrants(grants, new Map()),
    [grants]
  );

  const tenantRoleCommit = useCallback(
    (builtinRoleIds: string[], customRoleIds: string[]) => {
      if (!serviceAccountId) return;
      z.mutate(
        sharedMutators.setServiceAccountTenantRoles({
          serviceAccountId,
          builtinRoleIds,
          customRoleIds,
        })
      );
    },
    [z, serviceAccountId]
  );

  const {
    allTenantRoles,
    assignedBuiltinRoleIds,
    assignedCustomRoleIds,
    toggleTenantRole,
  } = useTenantRoles({
    grants,
    onCommit: tenantRoleCommit,
  });

  const addKey = async (name: string) => {
    const rawKey = generateApiKey();
    const hashedKey = await hashApiKey(rawKey);
    const start = rawKey.slice(0, 12);
    if (isCreateMode) {
      setBufferedKeys((prev) => [
        ...prev,
        { id: nanoid(), name, start, key: hashedKey },
      ]);
    } else if (account) {
      z.mutate(
        sharedMutators.createApiKey({
          id: nanoid(),
          name,
          start,
          key: hashedKey,
          spaceId: null,
          serviceAccountId: account.id,
        })
      );
    }
    return rawKey;
  };

  const removeKey = (id: string) => {
    if (isCreateMode)
      setBufferedKeys((prev) => prev.filter((k) => k.id !== id));
    else z.mutate(sharedMutators.deleteApiKey({ id }));
  };

  const removeFed = (id: string) => {
    if (isCreateMode)
      setBufferedFeds((prev) => prev.filter((f) => f.id !== id));
    else z.mutate(sharedMutators.deleteServiceAccountFederation({ id }));
  };

  const createServiceAccount = () => {
    const name = (draft ?? "").trim();
    if (!name) return;
    z.mutate(sharedMutators.createServiceAccount({ id: newId, name }));
    for (const k of bufferedKeys) {
      z.mutate(
        sharedMutators.createApiKey({
          id: k.id,
          name: k.name,
          start: k.start,
          key: k.key,
          spaceId: null,
          serviceAccountId: newId,
        })
      );
    }
    for (const f of bufferedFeds) {
      z.mutate(
        sharedMutators.createServiceAccountFederation({
          id: f.id,
          serviceAccountId: newId,
          providerId: f.providerId,
          name: f.name,
          subject: f.subject,
        })
      );
    }
    for (const clientId of bufferedOauthAppIds) {
      z.mutate(
        sharedMutators.authorizeOAuthAppForServiceAccount({
          id: nanoid(12),
          clientId,
          serviceAccountId: newId,
        })
      );
    }
    onClose();
  };

  const saveName = () => {
    if (!account || draft === null) return;
    const trimmed = draft.trim();
    if (trimmed && trimmed !== account.name) {
      z.mutate(
        sharedMutators.updateServiceAccount({ id: account.id, name: trimmed })
      );
    }
    setDraft(null);
  };

  if (!isCreateMode && !account) return null;

  const effectiveKeys: RenderKey[] = isCreateMode
    ? bufferedKeys.map((k) => ({
        id: k.id,
        name: k.name,
        start: k.start,
        createdAt: null,
        lastRequest: null,
        expiresAt: null,
      }))
    : keys.map((k) => ({
        id: k.id,
        name: k.name,
        start: k.start,
        createdAt: k.createdAt,
        lastRequest: k.lastRequest,
        expiresAt: k.expiresAt,
      }));

  const keyItems = effectiveKeys.map((k) => ({
    key: k.id,
    name: (
      <NameCell>
        <NeutralIconBadge iconName="key" />
        <NameCellText>
          <Text ellipsis variant="text-regular-md" color="var(--text-primary)">
            {k.name ?? "Service account key"}
          </Text>
          <Text ellipsis variant="text-small" color="var(--text-secondary)">
            {k.start ? `${k.start}…` : ""}
          </Text>
        </NameCellText>
      </NameCell>
    ),
    created: (
      <Text variant="text-small" color="var(--text-secondary)">
        {k.createdAt ? formatDateShort(k.createdAt) : "—"}
      </Text>
    ),
    lastUsed: (
      <Text variant="text-small" color="var(--text-secondary)">
        {k.lastRequest ? formatDateShort(k.lastRequest) : "never"}
      </Text>
    ),
    expires: (
      <Text variant="text-small" color="var(--text-secondary)">
        {k.expiresAt ? formatDateShort(k.expiresAt) : "never"}
      </Text>
    ),
    actions: (hasMouse: boolean) => (
      <div
        style={{ opacity: hasMouse ? 1 : 0 }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="secondary"
          icon="x"
          onClick={() =>
            isCreateMode
              ? removeKey(k.id)
              : setConfirm({
                  title: "Revoke API key",
                  description: `Any workload using “${k.name ?? "this key"}” will lose access immediately. This can’t be undone.`,
                  confirmLabel: "Revoke key",
                  onConfirm: () => removeKey(k.id),
                })
          }
        />
      </div>
    ),
  }));

  const effectiveFeds: RenderFed[] = isCreateMode
    ? bufferedFeds.map((f) => ({
        id: f.id,
        name: f.name,
        providerName: f.providerName,
        subject: f.subject,
        lastUsedAt: null,
      }))
    : (account?.federations ?? []).map((f) => ({
        id: f.id,
        name: f.name,
        providerName: f.provider?.name ?? "—",
        subject: f.subject,
        lastUsedAt: f.lastUsedAt,
      }));

  const federationItems = effectiveFeds.map((f) => ({
    key: f.id,
    name: (
      <NameCell>
        <NeutralIconBadge iconName="id-card-lanyard" />
        <Text ellipsis variant="text-regular-md" color="var(--text-primary)">
          {f.name}
        </Text>
      </NameCell>
    ),
    provider: (
      <Text ellipsis variant="text-small" color="var(--text-secondary)">
        {f.providerName}
      </Text>
    ),
    subject: (
      <Text ellipsis variant="text-small" color="var(--text-secondary)">
        {f.subject}
      </Text>
    ),
    lastUsed: (
      <Text variant="text-small" color="var(--text-secondary)">
        {f.lastUsedAt ? formatDateShort(f.lastUsedAt) : "never"}
      </Text>
    ),
    actions: (hasMouse: boolean) => (
      <div
        style={{ opacity: hasMouse ? 1 : 0 }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="secondary"
          icon="x"
          onClick={() =>
            isCreateMode
              ? removeFed(f.id)
              : setConfirm({
                  title: "Remove federation",
                  description: `Workloads federating via “${f.name}” will no longer be able to assume this service account.`,
                  confirmLabel: "Remove",
                  onConfirm: () => removeFed(f.id),
                })
          }
        />
      </div>
    ),
  }));

  // In create mode the authorized apps are the staged ids; otherwise the
  // persisted bindings. authorizedClientIds drives both the rendered list and
  // which apps remain offerable in the picker.
  const authorizedClientIds = isCreateMode
    ? bufferedOauthAppIds
    : oauthAppAuths.map((a) => a.clientId);
  const authorizedClientIdSet = new Set(authorizedClientIds);
  // Only apps an admin has registered and not yet authorized for this account.
  const authorizableApps = oauthApps.filter(
    (a) => !a.disabled && !authorizedClientIdSet.has(a.id)
  );
  const oauthMatches = authorizableApps.filter((a) => {
    const needle = oauthSearch.trim().toLowerCase();
    return !needle || a.name.toLowerCase().includes(needle);
  });
  const oauthAppName = (clientId: string) =>
    oauthApps.find((a) => a.id === clientId)?.name ?? clientId;

  const authorizeOAuthApp = (clientId: string) => {
    if (isCreateMode) {
      setBufferedOauthAppIds((prev) =>
        prev.includes(clientId) ? prev : [...prev, clientId]
      );
    } else {
      z.mutate(
        sharedMutators.authorizeOAuthAppForServiceAccount({
          id: nanoid(12),
          clientId,
          serviceAccountId: serviceAccountId ?? "",
        })
      );
    }
    setOauthSearch("");
    setShowOauthMatches(false);
    oauthInputRef.current?.blur();
  };

  const removeOauthApp = (clientId: string) => {
    if (isCreateMode) {
      setBufferedOauthAppIds((prev) => prev.filter((id) => id !== clientId));
      return;
    }
    const binding = oauthAppAuths.find((a) => a.clientId === clientId);
    if (binding) {
      z.mutate(
        sharedMutators.deauthorizeOAuthAppForServiceAccount({ id: binding.id })
      );
    }
  };

  const oauthAuthItems = authorizedClientIds.map((clientId) => ({
    key: clientId,
    // In create mode the app isn't persisted yet, so there's nothing to open;
    // once saved, clicking the row opens the app's detail/connection view.
    link: isCreateMode
      ? undefined
      : makeUrl(routes.modal.oauthApp, { id: clientId }),
    name: (
      <NameCell>
        <NeutralIconBadge iconName="id-card-lanyard" />
        <Text ellipsis variant="text-regular-md" color="var(--text-primary)">
          {oauthAppName(clientId)}
        </Text>
      </NameCell>
    ),
    actions: (hasMouse: boolean) =>
      canEdit ? (
        <div
          style={{ opacity: hasMouse ? 1 : 0 }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="secondary"
            icon="x"
            onClick={() =>
              isCreateMode
                ? removeOauthApp(clientId)
                : setConfirm({
                    title: "Remove app authorization",
                    description: `“${oauthAppName(clientId)}” will no longer be able to obtain new credentials for this service account. Existing credentials must be revoked separately.`,
                    confirmLabel: "Remove",
                    onConfirm: () => removeOauthApp(clientId),
                  })
            }
          />
        </div>
      ) : null,
  }));

  const tabs: ModalTab<Tab>[] = isCreateMode
    ? [{ key: "settings", label: "Settings", icon: "settings-2" }]
    : [
        { key: "settings", label: "Settings", icon: "settings-2" },
        { key: "access", label: "Access", icon: "lock" },
      ];

  return (
    <>
      <TabbedModal
        icon={
          isCreateMode ? (
            <NeutralIconBadge iconName="bot" />
          ) : (
            <ServiceAccountAvatar seed={account?.id ?? newId} />
          )
        }
        title={
          isCreateMode ? "New service account" : (draft ?? account?.name ?? "")
        }
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onClose={onClose}
        footerRight={
          isCreateMode ? (
            <div style={{ display: "flex", gap: 8 }}>
              <Button size="l" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                size="l"
                onClick={createServiceAccount}
                disabled={!(draft ?? "").trim()}
              >
                Create service account
              </Button>
            </div>
          ) : (
            <Button size="l" onClick={onClose}>
              Done
            </Button>
          )
        }
      >
        <TabPanel
          style={{ display: activeTab === "settings" ? "flex" : "none" }}
        >
          <Card>
            <FieldLabel>
              <Box horizontal={8} bottom={10}>
                <Text variant="text-small-md">Name</Text>
              </Box>
              <Input
                ref={nameRef}
                readOnly={account ? !canManage : false}
                placeholder="e.g. deploy-bot"
                value={draft ?? account?.name ?? ""}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={saveName}
                onKeyDown={
                  isCreateMode
                    ? (e) => {
                        if (e.key === "Enter") createServiceAccount();
                      }
                    : undefined
                }
                style={{ width: "100%" }}
              />
            </FieldLabel>
          </Card>

          {!isCreateMode &&
            canAssignTenantRoles &&
            allTenantRoles.length > 0 && (
              <Card>
                <SectionHeading>
                  <Text variant="text-regular-md">Tenant roles</Text>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Tenant-wide capabilities for this service account.
                  </Text>
                </SectionHeading>
                <TenantRolesChecklist
                  roles={allTenantRoles}
                  assignedBuiltinRoleIds={assignedBuiltinRoleIds}
                  assignedCustomRoleIds={assignedCustomRoleIds}
                  onToggle={toggleTenantRole}
                  disabled={!canManage}
                />
              </Card>
            )}

          <Card>
            <SectionHeading>
              <SectionHeadingRow>
                <SectionHeadingText>
                  <Text variant="text-regular-md">API keys</Text>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Secret keys that let a script or service act as this service
                    account. Treat them like passwords.
                  </Text>
                </SectionHeadingText>
                {canEdit && (
                  <Button
                    size="l"
                    variant="secondary"
                    icon="plus"
                    onClick={() => setShowGenerateKey(true)}
                  />
                )}
              </SectionHeadingRow>
            </SectionHeading>
            {effectiveKeys.length > 0 && (
              <ListBox data-compact="true" style={{ flex: "none" }}>
                <ListTable
                  headers={{
                    name: "Name",
                    created: "Created",
                    lastUsed: "Last used",
                    expires: "Expires",
                    actions: "",
                  }}
                  columns={{
                    created: "content",
                    lastUsed: "content",
                    expires: "content",
                  }}
                  items={keyItems}
                />
              </ListBox>
            )}
          </Card>

          {!isCreateMode && canAssume && (
            <Card>
              <SectionHeading>
                <SectionHeadingRow>
                  <SectionHeadingText>
                    <Text variant="text-regular-md">Temporary credentials</Text>
                    <Text variant="text-small" color="var(--text-secondary)">
                      Get a short-lived key that acts as this service account.
                      It expires on its own — no need to revoke it.
                    </Text>
                  </SectionHeadingText>
                  <Button
                    size="l"
                    variant="secondary"
                    icon="key"
                    onClick={() => setShowTempCredential(true)}
                  />
                </SectionHeadingRow>
              </SectionHeading>
            </Card>
          )}

          <Card>
            <SectionHeading>
              <SectionHeadingRow>
                <SectionHeadingText>
                  <Text variant="text-regular-md">Federation</Text>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Let a workload — like a GitHub Actions job — act as this
                    service account using a short-lived OIDC token, instead of a
                    stored key.
                  </Text>
                </SectionHeadingText>
                {canEdit && (
                  <Button
                    size="l"
                    variant="secondary"
                    icon="plus"
                    onClick={() => setShowAddFederation(true)}
                  />
                )}
              </SectionHeadingRow>
            </SectionHeading>
            {effectiveFeds.length > 0 && (
              <ListBox data-compact="true" style={{ flex: "none" }}>
                <ListTable
                  headers={{
                    name: "Name",
                    provider: "Provider",
                    subject: "Subject",
                    lastUsed: "Last used",
                    actions: "",
                  }}
                  columns={{
                    provider: "content",
                    subject: "flex",
                    lastUsed: "content",
                  }}
                  items={federationItems}
                />
              </ListBox>
            )}
          </Card>

          {oauthEnabled && (
            <Card>
              <SectionHeading>
                <Text variant="text-regular-md">OAuth apps</Text>
                <Text variant="text-small" color="var(--text-secondary)">
                  Which registered apps may obtain credentials for this service
                  account.
                </Text>
              </SectionHeading>
              {canEdit && (
                <SearchBar
                  ref={oauthSearchRef}
                  onMouseDown={(e) => {
                    const target = e.target as HTMLElement;
                    if (target.closest("button, input, [data-no-focus-steal]"))
                      return;
                    e.preventDefault();
                    oauthInputRef.current?.focus();
                  }}
                >
                  <SearchInputWrap>
                    <SearchIconWrap>
                      <Icon name="search" size={16} />
                    </SearchIconWrap>
                    <PlainInput
                      ref={oauthInputRef}
                      placeholder={
                        oauthApps.length === 0
                          ? "No OAuth apps registered — register one from the OAuth apps button above"
                          : "Authorize an app…"
                      }
                      value={oauthSearch}
                      onChange={(e) => {
                        setOauthSearch(e.target.value);
                        setShowOauthMatches(true);
                      }}
                      onFocus={() => setShowOauthMatches(true)}
                    />
                  </SearchInputWrap>
                  <AnchoredListPopover
                    anchorRef={oauthSearchRef}
                    open={showOauthMatches && oauthMatches.length > 0}
                    popoverRef={oauthPopoverRef}
                  >
                    <MatchSectionLabel>
                      <Text variant="text-small" color="var(--text-secondary)">
                        Apps
                      </Text>
                    </MatchSectionLabel>
                    {oauthMatches.map((a) => (
                      <MatchRow
                        key={a.id}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          authorizeOAuthApp(a.id);
                        }}
                      >
                        <MatchIcon>
                          <Icon name="id-card-lanyard" size={16} />
                        </MatchIcon>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Text
                            variant="text-regular"
                            color="var(--text-primary)"
                            ellipsis
                          >
                            {a.name}
                          </Text>
                        </div>
                      </MatchRow>
                    ))}
                  </AnchoredListPopover>
                </SearchBar>
              )}
              {authorizedClientIds.length > 0 && (
                <ListBox data-compact="true" style={{ flex: "none" }}>
                  <ListTable
                    headers={{ name: "Name", actions: "" }}
                    items={oauthAuthItems}
                  />
                </ListBox>
              )}
            </Card>
          )}

          {!isCreateMode && canManage && account && (
            <DangerSection
              title="Delete service account"
              description="Deleting this service account immediately revokes its API keys, federations, and access. This can’t be undone."
              actionLabel="Delete service account"
              confirm={{
                confirmLabel: "Delete",
                description: `Deleting “${account.name}” immediately revokes its API keys, federations, and access. This can’t be undone.`,
                onConfirm: () => {
                  z.mutate(
                    sharedMutators.deleteServiceAccount({ id: account.id })
                  );
                  onClose();
                },
              }}
            />
          )}
        </TabPanel>

        {!isCreateMode && (
          <TabPanel
            style={{ display: activeTab === "access" ? "flex" : "none" }}
          >
            <Card>
              <SectionHeading>
                <Text variant="text-regular-md">Access</Text>
                <Text variant="text-small" color="var(--text-secondary)">
                  What this service account can use, and its role in each. Grant
                  access from the space, connector, skill, network, or service
                  account itself.
                </Text>
              </SectionHeading>
              <PrincipalAccessTable
                grants={accessGrants}
                rolesMap={customRoleById}
                resolveScopeName={resolveScopeName}
                apps={appsById}
                emptyLabel="No access yet"
                emptyDescription="This service account hasn’t been granted access to anything."
                onOpenResource={openResource}
              />
            </Card>
          </TabPanel>
        )}
      </TabbedModal>

      {showGenerateKey && (
        <CreateApiKeyModal
          alert
          showAccess={false}
          onClose={() => setShowGenerateKey(false)}
          onCreateKey={addKey}
        />
      )}
      {showTempCredential && account && (
        <GetTemporaryCredentialModal
          serviceAccountId={account.id}
          onClose={() => setShowTempCredential(false)}
        />
      )}
      {showAddFederation && (
        <AddFederationModal
          serviceAccountId={account?.id ?? ""}
          onClose={() => setShowAddFederation(false)}
          onAdd={
            isCreateMode
              ? (f) =>
                  setBufferedFeds((prev) => [...prev, { id: nanoid(12), ...f }])
              : undefined
          }
        />
      )}
      {confirm && (
        <ConfirmModal
          open
          onClose={() => setConfirm(null)}
          onConfirm={() => {
            confirm.onConfirm();
            setConfirm(null);
          }}
          title={confirm.title}
          description={confirm.description}
          confirmLabel={confirm.confirmLabel}
          destructive
        />
      )}
    </>
  );
}
